import { IRange, languages } from 'monaco-editor-core';
import { LuceneModelParsedSchema, LuceneContext } from './interface';

export class LuceneCompletion {
  private static instance: LuceneCompletion;

  public static getInstance() {
    if (!this.instance) {
      this.instance = new LuceneCompletion();
    }

    return this.instance;
  }

  private constructor() {}

  public async doComplete(
    document: string,
    range: IRange,
    options: LuceneModelParsedSchema
  ): Promise<languages.CompletionList> {
    const context = this.getContext(document, range, options);

    // TODO: remove debug
    console.debug('context', context);

    // cases:
    // - `field:term _`
    // - `field:"phrase" _`
    if (
      !context.empty &&
      context.space &&
      context.operator === undefined &&
      (context.term !== undefined ||
        (context.phrase !== undefined && context.sign === undefined && context.quote === true) ||
        (context.term === undefined && context.phrase === undefined && context.quote === true))
    ) {
      return this.doCompleteForOperatorsOrImplicitTerms(context, range, options);
    }

    // cases:
    // - `field:_`
    // - `field:"_`
    // - `field:"phrase_`
    if (context.field !== undefined) {
      return this.doCompleteForFieldTermsOrFieldPhrases(context, range, options);
    }

    // cases:
    // - ``
    // - `field_`
    // - `term_`
    // - `"phrase_`
    // - `field:term operator_`
    // - `field:"phrase" operator_`
    return this.doCompleteForFieldsOrImplicitTermsOrOperators(context, range, options);
  }

  private async doCompleteForOperatorsOrImplicitTerms(
    context: LuceneContext,
    range: IRange,
    options: LuceneModelParsedSchema
  ) {
    const result: languages.CompletionList = {
      incomplete: false,
      suggestions: [],
    };

    // append operators
    for (const operator of options.operators.items) {
      result.suggestions.push({
        label: operator.name,
        kind: languages.CompletionItemKind.Operator,
        documentation: operator.description,
        insertText: `${operator.name} `,
        range: range,
      });
    }

    // temporarily disabled for this release
    //// append terms
    //for (const term of options.terms.items) {
    //  result.suggestions.push({
    //    label: term.name,
    //    kind: languages.CompletionItemKind.Keyword,
    //    documentation: term.description,
    //    insertText: `${term.name} `,
    //    range: range,
    //  });
    //}

    return result;
  }

  private async doCompleteForFieldTermsOrFieldPhrases(
    context: LuceneContext,
    range: IRange,
    options: LuceneModelParsedSchema
  ) {
    const field = options.fields.items.find(item => item.name === context.field);
    const result: languages.CompletionList = {
      incomplete: false,
      suggestions: [],
    };

    if (field) {
      if (Array.isArray(field.values)) {
        // append local terms for field
        for (const term of field.values) {
          result.suggestions.push({
            label: term.name,
            kind: languages.CompletionItemKind.Value,
            documentation: term.description,
            insertText: term.value ? `${term.value} ` : `${term.name} `,
            range: range,
          });
        }
      } else {
        // append remote terms for field
        const { incomplete, items } = await field.values.provideCompletionItems(context.phrase || context.term || '');
        result.incomplete = incomplete;
        for (const item of items) {
          result.suggestions.push({
            label: item.name,
            kind: languages.CompletionItemKind.Value,
            documentation: item.description,
            insertText: item.value ? `${item.value} ` : `${item.name} `,
            range: range,
          });
        }
      }
    } else {
      // temporarily disabled for this release
      //// append terms
      //for (const term of options.terms.items) {
      //  result.suggestions.push({
      //    label: term.name,
      //    kind: languages.CompletionItemKind.Keyword,
      //    documentation: term.description,
      //    insertText: context.phrase !== undefined ? `${term.name}` : `${term.name} `,
      //    range: range,
      //  });
      //}
    }

    return result;
  }

  private async doCompleteForFieldsOrImplicitTermsOrOperators(
    context: LuceneContext,
    range: IRange,
    options: LuceneModelParsedSchema
  ) {
    const result: languages.CompletionList = {
      incomplete: false,
      suggestions: [],
    };

    // append operators
    if (context.term !== undefined) {
      for (const operator of options.operators.items) {
        result.suggestions.push({
          label: operator.name,
          kind: languages.CompletionItemKind.Operator,
          documentation: operator.description,
          insertText: `${operator.name} `,
          range: range,
        });
      }
    }

    // temporarily disabled for this release
    //// append terms
    //if (
    //  context.empty ||
    //  context.term !== undefined ||
    //  (context.phrase === undefined && context.sign === undefined && context.space)
    //) {
    //  for (const term of options.terms.items) {
    //    result.suggestions.push({
    //      label: term.name,
    //      kind: languages.CompletionItemKind.Keyword,
    //      documentation: term.description,
    //      insertText: context.phrase !== undefined ? `${term.name}` : `${term.name} `,
    //      range: range,
    //    });
    //  }
    //}

    // also add fields to autocomplete if not phrase and not sign
    // `"abc" AND cat` in this case cat ca be a field or a term
    // `"abc" and -cat` in this case cat is definetly a term
    // `"abc" and "cat` in this case cat is also definetly not a field
    if (
      context.empty ||
      context.term !== undefined ||
      (context.phrase === undefined && context.sign === undefined && context.space)
    ) {
      for (const field of options.fields.items) {
        result.suggestions.push({
          label: field.name,
          kind: languages.CompletionItemKind.Keyword,
          documentation: field.description,
          insertText: this.getInsertTextForField(field.name, context.term || ""),
          range: range,
        });
      }
    }

    return result;
  }

  private getInsertTextForField(fieldName: string, contextTerm: string) {

    if (contextTerm) {
      if (contextTerm.indexOf(".") > 0 && fieldName.indexOf(".") > 0) {
        fieldName = fieldName.substr(fieldName.indexOf(".") + 1);
      }
      if (contextTerm.indexOf("&") > 0 && fieldName.indexOf("&") > 0) {
        fieldName = fieldName.substr(fieldName.indexOf("&") + 1);
      }
    }

    return fieldName + ": ";
  }

  private getContext(document: string, range: IRange, options: LuceneModelParsedSchema): LuceneContext {
    // initial regex was /((?<field>\w+)\s*:\s*)?((?<sign>-|\+)?(("(?<phrase>[^\"]*))|(?<term>[\w\*\?]*)))?$/
    // but it returned {phrase: ' abcd'} for input `"abcd" abc` instead of {term: 'abc'}
    // split into multiple regex for simplicity
    let fieldAndTerm, fieldAndPhrase, field, term, phrase;

    try {
      fieldAndTerm = RegExp('(?<field>\\w+(\\.[\\w\\&]+)*)\\s*:\\s*(?<sign>-|\\+)?(?<term>[\\w\\*\\?]*)$');
    } catch (error) {
      console.error(error);
    }

    try {
      fieldAndPhrase = RegExp('(?<field>\\w+(\\.[\\w\\&]+)*)\\s*:\\s*(?<sign>-|\\+)?"(?<phrase>[^\\"]*)$');
    } catch (error) {
      console.error(error);
    }

    try {
      field = RegExp('(?<field>\\w+(\\.[\\w\\&]+)*)\\s*:\\s*(?<sign>-|\\+)?$');
    } catch (error) {
      console.error(error);
    }

    try {
      term = RegExp('(?<sign>-|\\+)?(?<!\\")(?<term>[\\w\\*\\?\\.\\&]*)$'); // includes . and & for unknown-yet field
    } catch (error) {
      console.error(error);
    }

    try {
      phrase = RegExp('^[^\\"]*(\\"[^\\"]*\\")*[^\\"]*\\"(?<phrase>[^\\"]*)$');
    } catch (error) {
      console.error(error);
    }

    // ignore the opened bracket
    if (document.trim().endsWith('(')) {
      document = document.trim().slice(0, -1);
    }

    // mark whether it is empty
    if (document.trim().length === 0) {
      return {
        empty: true,
      };
    }

    // mark whether it ends with space
    let space = false;
    if (document.endsWith(' ')) {
      space = true;
      document = document.trim();
    }

    // mark whether it ends with quote
    let quote = false;
    if (document.endsWith('"')) {
      quote = true;
    }

    // check whether it is a `field:term_`
    let matches = document.match(fieldAndTerm);
    if (matches && matches['groups'] && matches['groups']['term'] && matches['groups']['field']) {
      return {
        ...matches['groups'],
        space: space,
        quote: quote,
      };
    }

    // check whether it is a `field:"phrase_`
    matches = document.match(fieldAndPhrase);
    if (
      matches &&
      matches['groups'] &&
      matches['groups']['phrase'] !== undefined &&
      matches['groups']['field'] !== undefined
    ) {
      return {
        ...matches['groups'],
        space: space,
        quote: quote,
      };
    }

    // check whether it is a `field:_`
    matches = document.match(field);
    if (matches && matches['groups'] && matches['groups']['field']) {
      return {
        ...matches['groups'],
        space: space,
        quote: quote,
      };
    }

    // check whether it is a `"phrase_`
    matches = document.match(phrase);
    if (matches && matches['groups'] && matches['groups']['phrase'] !== undefined) {
      return {
        ...matches['groups'],
        space: space,
        quote: quote,
      };
    }

    // check whether it is a ` operator_`
    // this also works for multi-word operators like "AND NOT"
    for (const item of options.operators.items) {
      if (document.toUpperCase().endsWith(` ${item.name}`)) {
        return {
          operator: item.name,
          space: space,
          quote: quote,
        };
      }
    }

    // check whether it is a `term_`
    matches = document.match(term);
    if (matches && matches['groups'] && matches['groups']['term'] !== undefined) {
      const termOrOperator = matches['groups']['term'];
      const operator = options.operators.items.find(item => item.name === termOrOperator);
      if (operator) {
        matches['groups']['operator'] = matches['groups']['term'];
        delete matches['groups']['term'];
      }

      return {
        ...matches['groups'],
        space: space,
        quote: quote,
      };
    }

    return {
      space: space,
      quote: quote,
    };
  }
}
